import { keepPreviousData, useQuery } from '@tanstack/react-query'
import {
  fetchBanner,
  fetchCoinInfo,
  fetchMarketTrend,
  fetchMetrics,
  fetchNewsList,
  fetchRankByAll,
  fetchRankByExchange
} from '@api/home/api.js'
import { useCallback } from 'react'

export const useMarketTrendQuery = ({
  trendTypes,
  staleTime = 1000 * 60 * 5,
  enabled = true
} = {}) => {
  return useQuery({
    queryKey: ['market-trend', { trendTypes }],
    queryFn: () => fetchMarketTrend(trendTypes ? { trendTypes } : {}),
    enabled,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    staleTime,
    placeholderData: keepPreviousData
  })
}

export const useMetricsQuery = (body = {}) => {
  return useQuery({
    queryKey: ['metrics', body],
    queryFn: () => fetchMetrics(body),
    enabled: true,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 60 * 24,
    placeholderData: keepPreviousData,
    select: useCallback(
      ({ daai }) => ({
        daai
      }),
      []
    )
  })
}

export const useBannerQuery = (body = {}) => {
  return useQuery({
    queryKey: ['banner', body],
    queryFn: () => fetchBanner(body),
    staleTime: Infinity,
    gcTime: Infinity,
    select: ({ banners }) => banners?.filter((banner) => banner.useYn === 'Y')
  })
}

export const useCoinInfoQuery = (body = {}) => {
  return useQuery({
    queryKey: ['home-coin-top', body],
    queryFn: () => fetchCoinInfo(body),
    enabled: true,
    staleTime: 1000 * 60 * 5,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData
  })
}

export const useRankByAllQuery = (body = {}) => {
  return useQuery({
    queryKey: ['rank-all', body],
    queryFn: () => fetchRankByAll(body),
    refetchOnWindowFocus: false, // 탭 전환 시 재요청 X
    refetchInterval: false,
    staleTime: 1000 * 60 * 5,
    placeholderData: keepPreviousData,
    select: useCallback(
      ({ lastFetchTime, all: { gainer, loser, marketCap, volume } }) => ({
        gainer,
        loser,
        marketCap,
        volume,
        lastFetchTime
      }),
      []
    )
  })
}

export const useRankByExchangeQuery = (body = {}) => {
  return useQuery({
    queryKey: ['rank-exchange', body],
    queryFn: () => fetchRankByExchange(body),
    refetchOnWindowFocus: true,
    refetchInterval: false,
    placeholderData: keepPreviousData,
    select: useCallback(
      (res) =>
        Object.fromEntries(
          Object.entries(res).filter(
            ([key, val]) =>
              ['upbit', 'digitalx', 'coinone', 'bithumb', 'gopax', 'lastFetchTime'].includes(key) &&
              val !== undefined
          )
        ),
      []
    )
  })
}

export const useNewsListQuery = ({ page = 1, count = 5 }) => {
  return useQuery({
    queryKey: ['news-list-home', page, count],
    queryFn: () => fetchNewsList({ page, count }),
    staleTime: Infinity, // 캐시 무제한 fresh
    gcTime: 1000 * 60 * 30, // 30분간 메모리에 유지
    placeholderData: keepPreviousData,
    select: useCallback(
      ({ newsVOS, lastFetchTime }) => ({
        newsVOS,
        lastFetchTime
      }),
      []
    ),
    refetchOnMount: 'always'
  })
}
